import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, X, ArrowRight } from 'lucide-react';
import Button from './ui/Button';

interface LimitReachedDialogProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'notes' | 'storage' | 'characters';
  limit: {
    current: number;
    max: number;
  };
}

const LimitReachedDialog: React.FC<LimitReachedDialogProps> = ({
  isOpen,
  onClose,
  type,
  limit
}) => {
  if (!isOpen) return null;

  const titles = {
    notes: 'Maximum Notes Reached',
    storage: 'Storage Limit Reached',
    characters: 'Character Limit Reached'
  };

  const descriptions = {
    notes: `You've reached your limit of ${limit.max} notes.`,
    storage: `You've reached your storage limit of ${limit.max} KB.`,
    characters: `You've reached your character limit of ${limit.max} characters.`
  };

  return (
    <div 
      className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        onClick={(e) => e.stopPropagation()}
        className="relative bg-card border rounded-lg shadow-lg max-w-md w-full p-6"
      >
        <Button
          variant="ghost"
          size="sm"
          className="absolute right-4 top-4"
          onClick={onClose}
        >
          <X className="w-4 h-4" />
        </Button>

        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 rounded-xl bg-destructive/10 flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-destructive" />
          </div>
          <div>
            <h3 className="text-lg font-semibold">{titles[type]}</h3>
            <p className="text-sm text-muted-foreground">{descriptions[type]}</p>
          </div>
        </div>

        <div className="bg-muted/50 rounded-lg p-4 mb-6">
          <div className="flex justify-between mb-2">
            <span className="text-sm">Current Usage</span>
            <span className="text-sm font-medium">{limit.current}/{limit.max}</span>
          </div>
          <div className="w-full bg-background rounded-full h-2">
            <div 
              className="bg-primary rounded-full h-2 transition-all"
              style={{ width: `${(limit.current / limit.max) * 100}%` }}
            />
          </div>
        </div>

        <div className="flex justify-end gap-4">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <a 
            href="/pricing"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Upgrade Plan
            <ArrowRight className="w-4 h-4 ml-2" />
          </a>
        </div>
      </motion.div>
    </div>
  );
};

export default LimitReachedDialog;